"use client"

import type React from "react"
import { useState } from "react"
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Clock, 
  Calendar,
  MessageSquare,
  Trash2,
  MoreVertical,
  Sparkles,
  BookOpen,
  History,
  X,
  Check
} from "lucide-react"

interface ChatHistory {
  id: string
  title: string
  date: "today" | "previous7days"
  timestamp: string
}

interface ChatHistorySidebarProps {
  isCollapsed?: boolean
  onToggle?: () => void
  onSelectChat?: (chatId: string) => void
  onDeleteChat?: (chatId: string) => void
  activeChat?: string
}

const mockChatHistory: ChatHistory[] = [
  {
    id: "1",
    title: "Solar System Facts",
    date: "today",
    timestamp: "2 hours ago",
  },
  {
    id: "2",
    title: "Math: Fractions Help",
    date: "today",
    timestamp: "4 hours ago",
  },
  {
    id: "3",
    title: "History of Computers",
    date: "previous7days",
    timestamp: "2 days ago",
  },
  {
    id: "4",
    title: "Photosynthesis Diagram",
    date: "previous7days",
    timestamp: "4 days ago",
  },
  {
    id: "5",
    title: "Creative Writing Tips",
    date: "previous7days",
    timestamp: "5 days ago",
  },
]

export function AIChatHistorySidebar({
  isCollapsed = false,
  onToggle,
  onSelectChat,
  onDeleteChat,
  activeChat = "1",
}: ChatHistorySidebarProps) {
  const [showDeleteMenu, setShowDeleteMenu] = useState<string | null>(null)
  const [deletingChat, setDeletingChat] = useState<string | null>(null)

  const todayChats = mockChatHistory.filter((chat) => chat.date === "today")
  const previousChats = mockChatHistory.filter((chat) => chat.date === "previous7days")

  const handleDeleteClick = (e: React.MouseEvent, chatId: string) => {
    e.stopPropagation()
    setDeletingChat(chatId)
    // Simulate deletion delay for UX
    setTimeout(() => {
      console.log("Deleting chat:", chatId)
      onDeleteChat?.(chatId)
      setDeletingChat(null)
      setShowDeleteMenu(null)
    }, 500)
  }

  const getRandomIcon = (id: string) => {
    const icons = [MessageSquare, BookOpen, Check, History, Calendar]
    const Icon = icons[parseInt(id) % icons.length]
    return <Icon size={16} />
  }

  return (
    <aside
      className={`flex flex-col transition-all duration-500 h-screen sticky top-0 ${
        isCollapsed 
          ? "w-20 bg-gradient-to-b from-white to-slate-50" 
          : "w-80 bg-gradient-to-b from-white via-white to-slate-50/50"
      } relative overflow-hidden border-r border-slate-100/50 shadow-sm`}
    >
      {/* Decorative Elements */}
      {!isCollapsed && (
        <>
          <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-teal-100/20 to-purple-100/20 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tr from-blue-50/30 to-cyan-50/30 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl" />
        </>
      )}

      {/* Header - Fixed */}
      <div className="relative z-10 p-6 border-b border-slate-100 flex items-center justify-between backdrop-blur-sm bg-white/50 shrink-0">
        {!isCollapsed && (
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl shadow-sm">
              <History className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900 text-lg tracking-tight">Chat History</h2>
              <p className="text-xs text-slate-500 font-medium">Learning sessions</p>
            </div>
          </div>
        )}
        <button
          onClick={onToggle}
          className="p-2.5 hover:bg-slate-100/80 rounded-xl transition-all duration-300 hover:scale-105 active:scale-95 shadow-sm border border-slate-200/50 bg-white"
          aria-label="Toggle sidebar"
        >
          {isCollapsed ? (
            <ChevronRight size={20} className="text-slate-600" />
          ) : (
            <ChevronLeft size={20} className="text-slate-600" />
          )}
        </button>
      </div>

      {/* New Chat Button - Fixed */}
      {!isCollapsed && (
        <div className="relative z-10 px-6 py-4 shrink-0">
          <button className="group w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white rounded-xl py-3 px-4 text-sm font-semibold flex items-center justify-center gap-3 transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/25 active:scale-[0.98]">
            <div className="p-1.5 bg-white/20 rounded-lg group-hover:scale-110 transition-transform duration-300">
              <Plus size={18} className="text-white" />
            </div>
            <span className="tracking-wide">New Learning Session</span>
          </button>
        </div>
      )}

      {/* Collapsed View - Scrollable */}
      {isCollapsed && (
        <div className="relative z-10 flex-1 overflow-hidden">
          <div className="h-full flex flex-col items-center py-6 space-y-4 overflow-y-auto scrollbar-thin scrollbar-thumb-blue-600 scrollbar-track-transparent hover:scrollbar-thumb-blue-700">
            <button className="p-3 bg-gradient-to-br from-teal-600 to-cyan-600 text-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105 active:scale-95">
              <Plus size={22} />
            </button>
            <div className="w-full px-3 space-y-2">
              {mockChatHistory.slice(0, 5).map((chat) => (
                <button
                  key={chat.id}
                  onClick={() => onSelectChat?.(chat.id)}
                  className={`w-12 h-12 flex items-center justify-center rounded-xl transition-all duration-300 ${
                    activeChat === chat.id
                      ? "bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-md"
                      : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200/50"
                  }`}
                  title={chat.title}
                >
                  {getRandomIcon(chat.id)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Expanded Chat History List - Scrollable */}
      {!isCollapsed && (
        <div className="relative z-10 flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto px-4 py-2 space-y-6 scrollbar-thin scrollbar-thumb-blue-600 scrollbar-track-transparent hover:scrollbar-thumb-blue-700">
            {/* Today Section */}
            {todayChats.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 px-2">
                  <div className="p-1.5 bg-teal-100/50 rounded-lg">
                    <Clock size={14} className="text-teal-600" />
                  </div>
                  <h3 className="text-xs font-bold text-teal-700 uppercase tracking-wider">Today</h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-teal-100/50 to-transparent" />
                </div>
                <div className="space-y-2">
                  {todayChats.map((chat) => (
                    <div
                      key={chat.id}
                      className={`relative group transition-all duration-300 ${
                        deletingChat === chat.id ? "opacity-50 scale-95" : ""
                      }`}
                    >
                      {deletingChat === chat.id && (
                        <div className="absolute inset-0 bg-gradient-to-r from-red-50 to-white/50 rounded-xl animate-pulse" />
                      )}
                      <button
                        onClick={() => {
                          console.log("Selected chat:", chat.id)
                          onSelectChat?.(chat.id)
                        }}
                        className={`w-full text-left p-4 rounded-xl text-sm flex items-start gap-4 transition-all duration-300 relative overflow-hidden border ${
                          activeChat === chat.id
                            ? "bg-gradient-to-r from-teal-50/80 to-cyan-50/50 border-teal-200/50 shadow-sm"
                            : "text-slate-700 hover:bg-white border-slate-200/50 hover:border-slate-300/50 hover:shadow-sm"
                        }`}
                      >
                        {/* Icon Container */}
                        <div className={`p-2.5 rounded-lg ${
                          activeChat === chat.id
                            ? "bg-gradient-to-br from-teal-500 to-cyan-500 text-white"
                            : "bg-slate-100 text-slate-600"
                        }`}>
                          {getRandomIcon(chat.id)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className={`font-medium truncate ${
                              activeChat === chat.id ? "text-teal-900" : "text-slate-900"
                            }`}>
                              {chat.title}
                            </h4>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setShowDeleteMenu(showDeleteMenu === chat.id ? null : chat.id)
                              }}
                              className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-slate-200/50 rounded-lg transition-all duration-200"
                            >
                              <MoreVertical size={16} className="text-slate-500" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Clock size={12} className="text-slate-400" />
                            <span className="text-xs text-slate-500 font-medium">{chat.timestamp}</span>
                          </div>
                        </div>

                        {/* Active Indicator */}
                        {activeChat === chat.id && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-gradient-to-b from-teal-500 to-cyan-500 rounded-r-full" />
                        )}
                      </button>

                      {/* Delete Menu */}
                      {showDeleteMenu === chat.id && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 z-20">
                          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-lg animate-in slide-in-from-right-2">
                            <button
                              onClick={(e) => handleDeleteClick(e, chat.id)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors group/delete"
                              title="Delete"
                            >
                              <Trash2 size={16} className="text-red-500 group-hover/delete:scale-110 transition-transform" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setShowDeleteMenu(null)
                              }}
                              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                              title="Cancel"
                            >
                              <X size={16} className="text-slate-500" />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Deleting Animation */}
                      {deletingChat === chat.id && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-r from-red-50/80 to-white/80 rounded-xl">
                          <div className="flex items-center gap-2 text-red-600 font-medium text-sm">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent" />
                            Deleting...
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Previous 7 Days Section */}
            {previousChats.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 px-2">
                  <div className="p-1.5 bg-slate-100/50 rounded-lg">
                    <Calendar size={14} className="text-slate-600" />
                  </div>
                  <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Previous 7 Days</h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-slate-100/50 to-transparent" />
                </div>
                <div className="space-y-2">
                  {previousChats.map((chat) => (
                    <div
                      key={chat.id}
                      className={`relative group transition-all duration-300 ${
                        deletingChat === chat.id ? "opacity-50 scale-95" : ""
                      }`}
                    >
                      <button
                        onClick={() => {
                          console.log("Selected chat:", chat.id)
                          onSelectChat?.(chat.id)
                        }}
                        className={`w-full text-left p-4 rounded-xl text-sm flex items-start gap-4 transition-all duration-300 relative overflow-hidden border ${
                          activeChat === chat.id
                            ? "bg-gradient-to-r from-teal-50/80 to-cyan-50/50 border-teal-200/50 shadow-sm"
                            : "text-slate-700 hover:bg-white border-slate-200/50 hover:border-slate-300/50 hover:shadow-sm"
                        }`}
                      >
                        {/* Icon Container */}
                        <div className={`p-2.5 rounded-lg ${
                          activeChat === chat.id
                            ? "bg-gradient-to-br from-teal-500 to-cyan-500 text-white"
                            : "bg-slate-100 text-slate-600"
                        }`}>
                          {getRandomIcon(chat.id)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className={`font-medium truncate ${
                              activeChat === chat.id ? "text-teal-900" : "text-slate-900"
                            }`}>
                              {chat.title}
                            </h4>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setShowDeleteMenu(showDeleteMenu === chat.id ? null : chat.id)
                              }}
                              className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-slate-200/50 rounded-lg transition-all duration-200"
                            >
                              <MoreVertical size={16} className="text-slate-500" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Calendar size={12} className="text-slate-400" />
                            <span className="text-xs text-slate-500 font-medium">{chat.timestamp}</span>
                          </div>
                        </div>
                      </button>

                      {/* Delete Menu */}
                      {showDeleteMenu === chat.id && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 z-20">
                          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-lg animate-in slide-in-from-right-2">
                            <button
                              onClick={(e) => handleDeleteClick(e, chat.id)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors group/delete"
                              title="Delete"
                            >
                              <Trash2 size={16} className="text-red-500 group-hover/delete:scale-110 transition-transform" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setShowDeleteMenu(null)
                              }}
                              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                              title="Cancel"
                            >
                              <X size={16} className="text-slate-500" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer - Fixed */}
      {!isCollapsed && (
        <div className="relative z-10 p-4 border-t border-slate-100/50 shrink-0">
          <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
            <span>Total sessions: {mockChatHistory.length}</span>
          </div>
        </div>
      )}

      {/* Custom scrollbar styles */}
      <style jsx>{`
        .scrollbar-thin::-webkit-scrollbar {
          width: 6px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background-color: #2563eb; /* blue-600 */
          border-radius: 3px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background-color: #1d4ed8; /* blue-700 */
        }
        .scrollbar-thin {
          scrollbar-width: thin;
          scrollbar-color: #2563eb transparent;
        }
      `}</style>
    </aside>
  )
}